import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Paper,
    Chip,
    Divider,
    Button,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Collapse,
    TextField,
    Alert,
} from "@mui/material";

const FreightCalculationDisplay = ({ calculationResult, onFreightPriceOverride }) => {
    const [showFreightDetails, setShowFreightDetails] = useState(false);
    const [overridePrice, setOverridePrice] = useState("");
    const [isOverridden, setIsOverridden] = useState(false);
    const [originalPrice, setOriginalPrice] = useState(null);

    if (!calculationResult) {
        return null;
    }

    const { totalFreightCost, baseFreightCost, fuelSurcharge, consignmentCharge, breakdown } =
        calculationResult;
    const fuelSurchargeAmount = (baseFreightCost * fuelSurcharge) / 100;

    const handleOverridePrice = () => {
        const newPrice = parseFloat(overridePrice);
        if (isNaN(newPrice) || newPrice < 0) {
            alert("Please enter a valid positive number");
            return;
        }

        if (!isOverridden) {
            setOriginalPrice(totalFreightCost);
        }

        setIsOverridden(true);

        // Call the parent component's callback to update the freight price
        if (onFreightPriceOverride) {
            onFreightPriceOverride(newPrice);
        }
    };

    const handleResetPrice = () => {
        setIsOverridden(false);
        setOverridePrice("");

        // Reset to original calculated price
        if (onFreightPriceOverride && originalPrice !== null) {
            onFreightPriceOverride(originalPrice);
        }
        setOriginalPrice(null);
    };

    const displayPrice = isOverridden ? parseFloat(overridePrice) : totalFreightCost;

    return (
        <Box>
            <Button
                variant="outlined"
                onClick={() => setShowFreightDetails(!showFreightDetails)}
                sx={{ mb: 2 }}
            >
                {showFreightDetails ? "Hide" : "Show"} Freight Price Info
            </Button>

            <Collapse in={showFreightDetails}>
                <Paper sx={{ p: 3, mb: 3 }}>
                    {/* Price Override Section */}
                    <Card variant="outlined" sx={{ mb: 3, bgcolor: "grey.50" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Freight Price Override
                            </Typography>

                            {isOverridden && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Freight price has been manually overridden. Original calculated price: $
                                    {originalPrice?.toFixed(2)}
                                </Alert>
                            )}

                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Override Freight Price"
                                        type="number"
                                        value={overridePrice}
                                        onChange={(e) => setOverridePrice(e.target.value)}
                                        placeholder={totalFreightCost.toFixed(2)}
                                        InputProps={{
                                            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                            inputProps: { min: 0, step: 0.01 },
                                        }}
                                        helperText="Enter new freight price to override calculation"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Button
                                        variant="contained"
                                        onClick={handleOverridePrice}
                                        disabled={!overridePrice}
                                        sx={{ mr: 1 }}
                                    >
                                        Override Price
                                    </Button>
                                    {isOverridden && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleResetPrice}
                                            color="secondary"
                                        >
                                            Reset to Calculated
                                        </Button>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Price: <strong>${displayPrice.toFixed(2)}</strong>
                                        {isOverridden && (
                                            <Chip
                                                label="OVERRIDDEN"
                                                color="warning"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Header */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Typography variant="h5" color="primary" gutterBottom>
                            Freight Calculation Breakdown
                        </Typography>
                        <Typography
                            variant="h4"
                            color={isOverridden ? "warning.main" : "success.main"}
                            gutterBottom
                        >
                            ${displayPrice.toFixed(2)}
                            {isOverridden && (
                                <Chip label="MANUAL OVERRIDE" color="warning" size="small" sx={{ ml: 2 }} />
                            )}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {isOverridden ? "Overridden" : "Calculated"} Freight Cost
                        </Typography>
                    </Box>

                    {/* Quick Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={3}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Shipping Type
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                    {breakdown.calculationType}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Load Type
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {breakdown.loadType}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Distance
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="secondary">
                                    {breakdown.distance.toFixed(1)} km
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Rate
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="warning.main">
                                    ${breakdown.rate.toFixed(2)}/km
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Show calculation details only if not overridden or if user wants to see original calculation */}
                    {!isOverridden && (
                        <>
                            {/* Main Content Grid */}
                            <Grid container spacing={3}>
                                {/* Left Column */}
                                <Grid item xs={12} lg={6}>
                                    {/* Shipment Dimensions */}
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Shipment Dimensions
                                            </Typography>

                                            <Chip
                                                label={breakdown.shipment.calculationPath}
                                                color="primary"
                                                variant="outlined"
                                                sx={{ mb: 2 }}
                                            />

                                            <Grid container spacing={2}>
                                                <Grid item xs={4}>
                                                    <Paper
                                                        sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            Width
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {breakdown.shipment.width.toFixed(2)}m
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Paper
                                                        sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            Height
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {breakdown.shipment.height.toFixed(2)}m
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Paper
                                                        sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}
                                                    >
                                                        <Typography variant="caption" color="text.secondary">
                                                            Length
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {breakdown.shipment.length.toFixed(2)}m
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>

                                    {/* Transport Information */}
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Transport Information
                                            </Typography>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell>Destination Zone:</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                            {breakdown.transportRate.zoneName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell>Postcode:</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                            {breakdown.transportRate.postcode}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell>Distance:</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                            {breakdown.distance.toFixed(2)} km
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </CardContent>
                                    </Card>

                                    {/* Available Rates */}
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Available Rates for Your Zone
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Quarter Load:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.quarterLoadRate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Half Load:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.halfLoadRate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Full Load:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.fullLoadRate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Wide 3.2m:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.wideLoad32Rate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Wide 3.5m:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.wideLoad35Rate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Paper sx={{ p: 1, bgcolor: "grey.50" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <Typography variant="caption">
                                                                Wide 4.0m:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold">
                                                                $
                                                                {breakdown.transportRate.wideLoad40Rate.toFixed(
                                                                    2
                                                                )}
                                                                /km
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Right Column */}
                                <Grid item xs={12} lg={6}>
                                    {/* Calculation Logic */}
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Calculation Logic
                                            </Typography>

                                            <Paper sx={{ p: 2, mb: 2, bgcolor: "warning.50" }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    color="warning.dark"
                                                    gutterBottom
                                                >
                                                    Why this rate was selected:
                                                </Typography>
                                                <Typography variant="body2" color="warning.dark">
                                                    {breakdown.explanation}
                                                </Typography>
                                            </Paper>

                                            <Paper sx={{ p: 2, bgcolor: "info.50" }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    color="info.dark"
                                                    gutterBottom
                                                >
                                                    Calculation Formula:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontFamily: "monospace", mb: 1 }}
                                                    color="info.dark"
                                                >
                                                    {breakdown.formula}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    color="info.dark"
                                                >
                                                    = ${baseFreightCost.toFixed(2)}
                                                </Typography>
                                            </Paper>
                                        </CardContent>
                                    </Card>

                                    {/* Cost Breakdown */}
                                    <Card variant="outlined" sx={{ mb: 2 }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Cost Breakdown
                                            </Typography>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    p: 2,
                                                    mb: 1,
                                                    bgcolor: "grey.50",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography>Base Freight Cost:</Typography>
                                                <Typography variant="h6" fontWeight="bold">
                                                    ${baseFreightCost.toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    p: 2,
                                                    mb: 1,
                                                    bgcolor: "warning.50",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography>
                                                    Fuel Surcharge ({fuelSurcharge.toFixed(1)}%):
                                                </Typography>
                                                <Typography fontWeight="bold" color="warning.dark">
                                                    + ${fuelSurchargeAmount.toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    p: 2,
                                                    mb: 2,
                                                    bgcolor: "info.50",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography>Consignment Charge:</Typography>
                                                <Typography fontWeight="bold" color="info.dark">
                                                    + ${consignmentCharge.toFixed(2)}
                                                </Typography>
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    p: 3,
                                                    bgcolor: "success.100",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="h6" fontWeight="bold">
                                                    Calculated Total:
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight="bold"
                                                    color="success.dark"
                                                >
                                                    ${totalFreightCost.toFixed(2)}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {/* Show message when price is overridden */}
                    {isOverridden && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The freight price has been manually overridden to $
                            {parseFloat(overridePrice).toFixed(2)}. The calculation details above show the
                            original calculated breakdown.
                        </Alert>
                    )}
                </Paper>
            </Collapse>
        </Box>
    );
};

export default FreightCalculationDisplay;
